/**
 * useCustomerPermissions Hook
 *
 * Centralized permission checks for customer-related actions.
 * Two levels of control:
 * 1. Customer-level: When customer is inactive, most actions are disabled
 * 2. Item-level: When a specific item (contact/location) is inactive, edit/set-default are disabled
 *
 * Designed to be extended for role-based access control (RBAC) in the future.
 *
 * Usage:
 * // Detail page (edit mode)
 * const { canEdit, canAdd, getItemPermissions } = useCustomerPermissions(customer);
 *
 * // Form wizard - pass isCreateMode to enable all actions in create mode
 * const { getItemPermissions } = useCustomerPermissions(customer, { isCreateMode: !isEditMode });
 *
 * <Button disabled={!canEdit}>Edit Customer</Button>
 * <Button disabled={!getItemPermissions(record).canEdit}>Edit Item</Button>
 */

import {
  getToggleStatusStyle as sharedGetToggleStatusStyle,
  calculateCustomerItemPermissions,
  type CustomerItemPermissionInput,
  type CustomerItemPermissions,
  type ToggleStatusStyleConfig,
} from '@/utils/permissionUtils';
import type { GlobalToken } from 'antd';

// Re-export types for backward compatibility
export type { ToggleStatusStyleConfig };

export interface CustomerPermissionInput {
  isActive?: boolean;
}

export interface PermissionOptions {
  /** When true, treats entity as active (for create mode in wizards) */
  isCreateMode?: boolean;
}

export type ItemPermissionInput = CustomerItemPermissionInput;

export interface CustomerPermissions {
  /** Whether the customer can be edited (header, financial info, etc.) */
  canEdit: boolean;
  /** Whether new items can be added to the customer (contacts, locations) */
  canAdd: boolean;
  /** Whether items can be deleted from the customer */
  canDelete: boolean;
  /** Whether the customer status can be toggled */
  canToggleStatus: boolean;
  /** Get item-level permissions for a specific record in a table */
  getItemPermissions: (item: ItemPermissionInput | null) => ItemPermissions;
}

export type ItemPermissions = CustomerItemPermissions;

/**
 * Get toggle status action style configuration
 * Use this to ensure consistent styling across all toggle status actions
 *
 * @param itemPerms - Item permissions from getItemPermissions()
 * @param token - Ant Design theme token
 * @returns Style configuration for toggle status action
 *
 * @example
 * const itemPerms = getItemPermissions(record);
 * const toggleStyle = getToggleStatusStyle(itemPerms, token);
 * {
 *   key: 'toggleStatus',
 *   label: itemPerms.isItemActive ? t('customer.actions.deactivate') : t('customer.actions.activate'),
 *   danger: toggleStyle.danger,
 *   disabled: toggleStyle.disabled,
 *   style: toggleStyle.style,
 * }
 */
export function getToggleStatusStyle(
  itemPerms: ItemPermissions,
  token: GlobalToken
): ToggleStatusStyleConfig {
  return sharedGetToggleStatusStyle(itemPerms, token);
}

/**
 * Hook to check customer-level permissions and get item-level permission checker
 *
 * @param customer - Customer object with at least isActive field, or null
 * @param options - Optional configuration
 * @param options.isCreateMode - When true, treats customer as active (for wizards in create mode)
 * @returns Permission flags for customer actions and a function to get item permissions
 *
 * @example
 * // In CustomerDetailPage
 * const { canEdit, canAdd, getItemPermissions } = useCustomerPermissions(customer);
 *
 * // In CustomerFormWizard (handles both create and edit mode)
 * const { getItemPermissions } = useCustomerPermissions(existingCustomer, { isCreateMode: !isEditMode });
 *
 * // Disable edit button when customer is inactive
 * <Button disabled={!canEdit}>{t('customer.actions.edit')}</Button>
 *
 * // In table action column - disable based on both customer AND item status
 * {
 *   key: 'edit',
 *   disabled: !getItemPermissions(record).canEdit,
 * }
 */
export function useCustomerPermissions(
  customer: CustomerPermissionInput | null | undefined,
  options?: PermissionOptions
): CustomerPermissions {
  // In create mode, treat customer as active since we're creating a new one
  const customerIsActive = options?.isCreateMode ? true : (customer?.isActive ?? false);

  // Status-based permissions
  // When customer is inactive, editing and adding are disabled
  // Future: Add role-based checks here
  // e.g., const { user } = useAuth();
  // const hasEditRole = user?.roles?.includes('customer:edit');

  /**
   * Get permissions for a specific item (contact or location)
   * Combines customer-level and item-level status checks
   */
  const getItemPermissions = (item: ItemPermissionInput | null): ItemPermissions => {
    return calculateCustomerItemPermissions(customerIsActive, item);
  };

  return {
    canEdit: customerIsActive,
    canAdd: customerIsActive,
    canDelete: customerIsActive,
    canToggleStatus: true, // Always allow toggle (to reactivate customer)
    getItemPermissions,
  };
}

/**
 * @deprecated Use useCustomerPermissions().getItemPermissions() instead
 * Hook to check item-level permissions within a customer
 * Used for contacts and locations tables
 */
export function useCustomerItemPermissions(
  customer: CustomerPermissionInput | null | undefined,
  item: ItemPermissionInput | null
): ItemPermissions {
  const { getItemPermissions } = useCustomerPermissions(customer);
  return getItemPermissions(item);
}

export default useCustomerPermissions;
